import { useCallback, useEffect, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { userProfileState } from "@/store/user-profile";
import {
  ENotificationType,
  systemNotificationsState,
} from "@/store/system-notifications";
import { useSnackBar } from "@/hooks/use-snack-bar";
import { useGomakeAxios } from "@/hooks/use-gomake-axios";
import { usePolling } from "@/hooks/polling/use-polling";
import { POLLING_INTERVALS } from "@/hooks/polling/polling-intervals";
import { getUserToken } from "@/services/storage-data";
import { EHttpMethod } from "@/services/api-service/enums";
import {
  clearPendingDocumentReportJob,
  getPendingDocumentReportJobIds,
  hasPendingDocumentReportJob,
  saveDocumentReportReadyNotification,
} from "@/utils/document-report-job";

export type DocumentReportReadyPayload = {
  jobId?: string;
  status?: "ready" | "failed" | string;
  downloadUrl?: string | null;
  errorMessage?: string | null;
  documentType?: number;
};

/**
 * Listens for DocumentReportReady-{userId}.
 * On ready: adds a bell notification (click to download). Does not auto-download.
 */
const useDocumentReportReadySocket = () => {
  const { t } = useTranslation();
  const userProfile = useRecoilValue(userProfileState);
  const userId = userProfile?.userId || userProfile?.id;
  const { setSnackbarStateValue } = useSnackBar();
  const setNotifications = useSetRecoilState(systemNotificationsState);
  const { callApi } = useGomakeAxios();
  const handledJobsRef = useRef<Set<string>>(new Set());

  const handlePayload = useCallback(
    (payload: DocumentReportReadyPayload) => {
      const jobId = payload?.jobId ? String(payload.jobId) : "";
      if (jobId && handledJobsRef.current.has(jobId)) {
        return;
      }
      if (
        jobId &&
        hasPendingDocumentReportJob() &&
        !hasPendingDocumentReportJob(jobId)
      ) {
        return;
      }

      if (payload?.status === "ready" && payload?.downloadUrl) {
        if (jobId) {
          handledJobsRef.current.add(jobId);
          clearPendingDocumentReportJob(jobId);
        }

        const notificationId = jobId || `report-${Date.now()}`;
        const message = t("sales.list.reportGenerationReadyNotification");
        const createdDate = new Date().toISOString();
        const notification = {
          id: notificationId,
          type: ENotificationType.DOCUMENT_REPORT_READY,
          message,
          url: payload.downloadUrl,
          createdDate,
        };

        saveDocumentReportReadyNotification({
          id: notificationId,
          type: ENotificationType.DOCUMENT_REPORT_READY,
          message,
          url: payload.downloadUrl,
          createdDate,
        });

        setNotifications((prev) => {
          if (prev.some((n) => n.id === notificationId)) return prev;
          return [notification, ...prev];
        });

        setSnackbarStateValue({
          state: true,
          message: t("sales.list.reportGenerationReady"),
          type: "success",
        });
        return;
      }

      if (payload?.status === "failed") {
        if (jobId) {
          handledJobsRef.current.add(jobId);
          clearPendingDocumentReportJob(jobId);
        }
        setSnackbarStateValue({
          state: true,
          message:
            payload?.errorMessage || t("sales.list.reportGenerationFailed"),
          type: "error",
        });
      }
    },
    [setNotifications, setSnackbarStateValue, t]
  );

  const hasToken = typeof window !== "undefined" && !!getUserToken();

  usePolling({
    enabled: !!userId && hasToken,
    intervalMs: POLLING_INTERVALS.documentReport,
    events: userId
      ? {
          [`DocumentReportReady-${userId}`]: (data) => {
            handlePayload((data || {}) as DocumentReportReadyPayload);
          },
        }
      : {},
  });

  // Fallback: poll job status for any pending jobIds in sessionStorage
  useEffect(() => {
    if (!hasToken) return;

    let cancelled = false;

    const pollPendingJobs = async () => {
      if (cancelled) return;
      const pending = getPendingDocumentReportJobIds();

      for (const jobId of pending) {
        if (cancelled || handledJobsRef.current.has(jobId)) continue;
        try {
          const res = await callApi(
            EHttpMethod.GET,
            `/v1/report-generation/jobs/${encodeURIComponent(jobId)}`,
            {},
            false
          );
          if (!res?.success) continue;
          const job = res?.data?.data ?? res?.data;
          if (!job?.status) continue;
          handlePayload({
            jobId,
            status: job.status,
            downloadUrl: job.downloadUrl,
            errorMessage: job.errorMessage,
            documentType: job.documentType,
          });
        } catch {
          // ignore; next tick retries
        }
      }
    };

    const intervalId = window.setInterval(() => {
      void pollPendingJobs();
    }, 10000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [callApi, handlePayload, hasToken]);
};

export { useDocumentReportReadySocket };
